// components/ActionDialog.tsx
import React, { useMemo, useState } from "react";
import { Dialog, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { toastError, toastSuccess } from "@/lib/toast";
import { ResponsiveDialogContent } from "@/components/ResponsiveDialogContent";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { DatePickerField } from "./DatePickerField";

type FieldType = "text" | "number" | "date" | "textarea" | "select";

export type FieldDef = {
  name: string;
  label: string;
  type: FieldType;
  placeholder?: string;
  required?: boolean;
  readonly?: boolean;
  options?: Array<{ value: string; label: string }>; // for select
  colSpan?: 1 | 2; // grid span

  // ✅ only for date fields
  min?: Date;
  max?: Date;
  defaultMonth?: Date;
};

export function ActionDialog({
  open,
  onOpenChange,
  title,
  endpoint,
  method = "POST",
  fields,
  defaultValues,
  submitLabel = "Process",
  transform,
  onSuccess,
  onError,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  title: string;
  endpoint: string;
  method?: "POST" | "PUT" | "PATCH";
  fields: FieldDef[];
  defaultValues?: Record<string, any>;
  submitLabel?: string;

  /** Optional payload mapper (e.g. map status -> note_status, add customer_id, cast numbers) */
  transform?: (payload: Record<string, any>) => Record<string, any>;

  /** Called after a successful response (res.ok !== false) */
  onSuccess?: (res: any) => void;

  /** Called when backend responds with ok=false */
  onError?: (res: any) => void;
}) {
  const initial = useMemo(() => {
    const base: Record<string, any> = {};
    fields.forEach((f) => (base[f.name] = defaultValues?.[f.name] ?? ""));
    return base;
  }, [fields, defaultValues]);

  const [form, setForm] = useState<Record<string, any>>(initial);
  const [saving, setSaving] = useState(false);

  // Reset when opening (or when defaults change)
  React.useEffect(() => {
    if (open) setForm(initial);
  }, [open, initial]);

  const canSubmit = useMemo(() => {
    return fields.every((f) => (f.required ? String(form[f.name] ?? "").trim().length > 0 : true));
  }, [fields, form]);

  async function submit() {
    if (!canSubmit || saving) return;

    setSaving(true);
    try {
      const payload = transform ? transform(form) : form;

      const res = await apiFetch<any>(endpoint, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (res?.ok === false) {
        toastError(res?.message ?? "Failed");
        onError?.(res);
        return;
      }

      toastSuccess(res?.message ?? "Done");
      onOpenChange(false);
      onSuccess?.(res);
    } catch (e: any) {
      toastError(e?.message ?? "Failed");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <ResponsiveDialogContent>
        <div className="border-b border-border bg-muted/30 px-5 py-4">
          <DialogHeader>
            <DialogTitle className="text-xl">{title}</DialogTitle>
          </DialogHeader>
        </div>

        <div className="px-5 py-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {fields.map((f) => {
              const span = f.colSpan === 2 ? "md:col-span-2" : "";
              const v = form[f.name] ?? "";
              const ro = !!f.readonly;

              return (
                <div key={f.name} className={span}>
                  <Label className="text-sm font-medium">
                    {f.label} {f.required ? <span className="text-destructive">*</span> : null}
                  </Label>

                  {f.type === "textarea" ? (
                    <Textarea
                      className={cn("mt-2 min-h-[110px]", ro && "bg-muted text-muted-foreground")}
                      placeholder={f.placeholder}
                      value={v}
                      readOnly={ro}
                      disabled={ro}
                      onChange={(e) => setForm((p) => ({ ...p, [f.name]: e.target.value }))}
                    />
                  ) : f.type === "select" ? (
                    <div className="mt-2">
                      <Select
                        value={String(v)}
                        disabled={ro}
                        onValueChange={(val) => setForm((p) => ({ ...p, [f.name]: val }))}
                      >
                        <SelectTrigger className={cn(ro && "bg-muted text-muted-foreground")}>
                          <SelectValue placeholder={f.placeholder ?? "Select"} />
                        </SelectTrigger>
                        <SelectContent>
                          {(f.options ?? []).map((o) => (
                            <SelectItem key={o.value} value={o.value}>
                              {o.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ) : f.type === "date" ? (
                    <div className="mt-2">
                      <DatePickerField
                        value={String(v)}
                        onChange={(val) => setForm((p) => ({ ...p, [f.name]: val }))}
                        placeholder={f.placeholder ?? "Pick a date"}
                        min={f.min}
                        max={f.max}
                        defaultMonth={f.defaultMonth}
                        disabled={ro}
                        error={undefined}
                      />
                    </div>
                  ) : (
                    <Input
                      className={cn("mt-2", ro && "bg-muted text-muted-foreground")}
                      type={f.type}
                      placeholder={f.placeholder}
                      value={v}
                      readOnly={ro}
                      disabled={ro}
                      onChange={(e) => setForm((p) => ({ ...p, [f.name]: e.target.value }))}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="border-t border-border px-5 py-4">
          <DialogFooter className="gap-2 sm:gap-3">
            <Button variant="outline" type="button" onClick={() => onOpenChange(false)} disabled={saving}>
              Close
            </Button>
            <Button type="button" disabled={!canSubmit || saving} onClick={submit}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              {submitLabel}
            </Button>
          </DialogFooter>
        </div>
      </ResponsiveDialogContent>
    </Dialog>
  );
}